import {reservationTicketEmail} from '../../utils/reservation-ticket';
﻿import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { TranslateService } from "@ngx-translate/core";
import {
  cilCheckCircle,
  cilSave,
  cilWarning,
  cilXCircle,
} from "@coreui/icons";
import { NgForm } from "@angular/forms";
import { format } from "date-fns";
import { GuestEditSettingsService } from "../../services/guest-edit-settings.service";
import {
  BookingFormGuard,
  BookingFormGuardsService,
} from "../../services/booking-form-guards.service";
import {
  BookingFormSettings,
  BookingFormSettingsService,
} from "../../services/booking-form-settings.service";
import { AltchaComponent } from "../../components/altcha/altcha.component";
import { nearestFitCapacity, isNearestFitTable } from "../../utils/table-fit";
import { isCompletedRefusal, isCompletedReservation } from "../../utils/reservation-status";

interface Session {
  id: number;
  user_id: number;
  name: string;
  start_time: string;
  end_time: string;
  hall_id: number;
}

interface SalonOption {
  salon_name: string;
}

interface SalonTable {
  table_id: string;
  capacity?: number;
  is_combined?: boolean;
  source_table_ids?: string[];
  combination_id?: number;
}

interface TableAvailabilityDot {
  tableId: string;
  isAvailable: boolean;
  capacity?: number;
  /** Big enough for the party (capacity >= party size). */
  canFitParty: boolean;
  /** Big enough AND close to the best available fit — the only selectable set. */
  isNearestFit: boolean;
  /** Public pages never expose a custom joined-table id, which may be a guest name. */
  isCombined?: boolean;
  joinedTableIndex?: number;
}

@Component({
    selector: "app-edit-reservation",
    templateUrl: "./edit-reservation.component.html",
    styleUrls: ["./edit-reservation.component.scss"],
    standalone: false
})
export class EditReservationComponent implements OnInit {
  private ticketLink:string|null=null;
  @Input() ticketContext?: {companyName:string;reservationId:string;language?:string};
  @ViewChild(AltchaComponent) altcha?: AltchaComponent;
  icons = { cilSave, cilXCircle, cilCheckCircle, cilWarning };
  public today = new Date();
  currentStep = 1;
  readonly totalSteps = 3;
  stepValidationMessage = "";

  companyName!: string;
  reservationId!: string;
  userId!: string;

  /**
   * defaultplus tenants share this `/edit` route (their confirmation emails now
   * link here too) but get the appointment edit form rendered by the embedded
   * child; the restaurant table-edit flow below stays untouched.
   */
  isDefaultPlus = false;

  name: string = "";
  num_people: number | null = null;
  time: string = "";
  date: any = "";
  service: string = "";
  selectedSalon: string = "";
  email: string = "";
  phoneNumber: string = "";

  isLoading = false;
  success: boolean | null = null;
  errorMessage = "";
  captchaToken: string | null = null;
  captchaReady = false;

  // Guest-edit-disable: when the tenant switched the edit page off we show a
  // polite in-component state instead of the form. The backend gate (403
  // EDIT_DISABLED) is the real enforcement; this is the friendly layer.
  guestEditDisabled = false;
  disabledMessage: string | null = null;
  guestTableSelectionEnabled = true;
  pastReservation = false;

  salonList: SalonOption[] = [];
  baseSalons: SalonOption[] = [];

  sessions: Session[] = [];
  baseSessions: Session[] = [];
  selectedSessionId: number | null = null;
  availableTimes: string[] = [];
  selectedTableId: string | null = null;

  tableAvailabilityDots: TableAvailabilityDot[] = [];
  tableAvailabilitySummary = { total: 0, available: 0, occupied: 0 };
  tableAvailabilityMessageKey: string | null = null;
  isTableAvailabilityLoading = false;

  guards: BookingFormGuard[] = [];
  guardConsents: Record<number, boolean> = {};
  guardsLoaded = false;
  formSettings: BookingFormSettings = {
    max_people: null,
    max_people_message: null,
  };

  allClosures: any[] = [];
  fullyClosedDates: Date[] = [];
  closedSessionKeys: Set<string> = new Set();
  isSelectedDateFullyClosed = false;
  selectedDateHasClosures = false;

  private closedWeekdays: Record<string, boolean> = {};
  private readonly weekdayKeys = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  dateFilter: (date: Date) => boolean = () => true;

  private selectedSalonTables: SalonTable[] = [];
  private salonTablesCache: Record<string, SalonTable[]> = {};
  private reservationRows: any[] = [];
  private sessionRequestToken = 0;
  private tableAvailabilityRequestToken = 0;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
    private guestEditSettings: GuestEditSettingsService,
    private guardsService: BookingFormGuardsService,
    private settingsService: BookingFormSettingsService
  ) {
    const supportedLanguages = ['en', 'tr', 'es', 'el', 'de'];
    this.translate.addLangs(supportedLanguages);
    this.translate.setDefaultLang('en');
    const browserLang = navigator.language.split('-')[0];
    this.translate.use(supportedLanguages.includes(browserLang) ? browserLang : 'en');
  }

  ngOnInit(): void {
    if (this.ticketContext) {
      if(this.ticketContext.language)this.translate.use(this.ticketContext.language);
      this.companyName=this.ticketContext.companyName;
      this.reservationId=this.ticketContext.reservationId;
      this.fetchUserIdAndReservation();
      return;
    }

    this.route.paramMap.subscribe((params) => {
      const company = params.get("companyName");
      const id = params.get("reservationId");

      if (company && id) {
        this.companyName = decodeURIComponent(company);
        this.reservationId = id;
        this.fetchUserIdAndReservation();
      } else {
        this.success = false;
      }
    });
  }

  fetchUserIdAndReservation(): void {
    this.isLoading = true;
    const url = `${environment.baseUrl}/users/get-id-by-company-name`;

    this.http
      .get<any>(url, { params: { company_name: this.companyName } })
      .subscribe({
        next: (res) => {
          this.userId = res.user_id;
          this.isDefaultPlus =
            String(res.company_type || '').trim().toLowerCase() === 'defaultplus';
          if (this.isDefaultPlus) {
            // The embedded appointment edit form owns the dp flow entirely.
            this.isLoading = false;
            return;
          }
          this.checkGuestEditEnabled();
        },
        error: () => {
          this.success = false;
          this.isLoading = false;
        },
      });
  }

  /**
   * Ask the backend whether this tenant allows guest edits BEFORE loading the
   * form data. Disabled → polite state, nothing else fetched. A failed lookup
   * fails open and loads the form — the backend gate still protects the save.
   */
  private checkGuestEditEnabled(): void {
    this.guestEditSettings
      .getPublicSettings(this.userId, this.translate.currentLang)
      .subscribe({
        next: (settings) => {
          if (settings && settings.edit_enabled === false) {
            this.guestEditDisabled = true;
            this.disabledMessage = settings.disabled_message || null;
            this.isLoading = false;
            return;
          }
          this.guestTableSelectionEnabled =
            settings?.guest_table_selection_enabled !== false;
          if (!this.guestTableSelectionEnabled) {
            this.selectedTableId = null;
            this.clearTableAvailabilityState();
          }
          this.loadFormData();
        },
        error: () => {
          this.loadFormData();
        },
      });
  }

  private loadFormData(): void {
    this.loadGuards();
    this.loadFormSettings();
    this.loadSalonList();
    this.loadSessions();
    this.loadClosures();
    this.loadClosedWeekdays();
  }

  private loadGuards(): void {
    this.guardsLoaded = false;
    const lang = this.translate.currentLang || "en";
    this.guardsService.getPublicGuards(Number(this.userId), lang).subscribe({
      next: (guards) => {
        this.guards = guards;
        // An update is a new acknowledgement event. Never pre-check notices
        // from the original reservation.
        this.guardConsents = {};
        this.guardsLoaded = true;
      },
      error: (err) => {
        console.error("[EditReservation] Failed to load booking notices:", err);
        this.guards = [];
        this.guardConsents = {};
        // Match the manual form's fail-open handling when the optional notice
        // service is unavailable, but never while the request is still pending.
        this.guardsLoaded = true;
      },
    });
  }

  private loadFormSettings(): void {
    this.settingsService.getPublicSettings(Number(this.userId)).subscribe({
      next: (settings) => {
        this.formSettings = settings || {
          max_people: null,
          max_people_message: null,
        };
      },
      error: (err) => {
        console.error("[EditReservation] Failed to load form settings:", err);
        this.formSettings = {
          max_people: null,
          max_people_message: null,
        };
      },
    });
  }

  private closureDateToKey(raw: string): string {
    const value = String(raw || "").trim();
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(value)) {
      const [day, month, year] = value.split(/[./]/);
      return `${year}-${month}-${day}`;
    }
    return value.slice(0, 10);
  }

  loadClosedWeekdays(): void {
    const url = `${environment.baseUrl}/closed-days/get?user_id=${this.userId}`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.closedWeekdays = res?.data || {};
        // A new function identity makes the date picker recompute its cells.
        this.dateFilter = (date: Date) => !this.isWeeklyClosed(date);
        this.clearSelectedDateIfClosed();
        this.refreshClosureState();
      },
      // No row means that recurring closures have not been configured.
      error: () => {
        this.closedWeekdays = {};
      },
    });
  }

  private isWeeklyClosed(date: Date | null): boolean {
    if (!date || isNaN(date.getTime())) return false;
    return !!this.closedWeekdays[this.weekdayKeys[date.getDay()]];
  }

  loadClosures(): void {
    const url = `${environment.baseUrl}/closed-dates/get/${this.userId}`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.allClosures = res?.data || [];
        this.recomputeFullyClosedDates();
        this.clearSelectedDateIfClosed();
        this.refreshClosureState();
      },
      error: (err) => {
        console.error("[EditReservation] Failed to load closure data:", err);
      },
    });
  }

  private isDayFullyClosed(dateClosures: any[]): boolean {
    if (dateClosures.some((entry) => !entry.salon_name && !entry.session_name)) {
      return true;
    }

    const salonNames = this.baseSalons
      .map((salon) => String(salon.salon_name || "").trim().toLowerCase())
      .filter((name) => !!name);
    if (!salonNames.length) return false;

    const closedSalons = new Set(
      dateClosures
        .filter((entry) => entry.salon_name && !entry.session_name)
        .map((entry) => String(entry.salon_name).trim().toLowerCase())
    );
    return salonNames.every((name) => closedSalons.has(name));
  }

  private recomputeFullyClosedDates(): void {
    const closuresByDate = new Map<string, any[]>();
    for (const closure of this.allClosures) {
      const key = this.closureDateToKey(closure.closed_date);
      if (!key) continue;
      const rows = closuresByDate.get(key);
      rows ? rows.push(closure) : closuresByDate.set(key, [closure]);
    }

    const dates: Date[] = [];
    closuresByDate.forEach((rows, key) => {
      if (!this.isDayFullyClosed(rows)) return;
      const parts = key.split("-");
      if (parts.length !== 3) return;
      dates.push(
        new Date(+parts[0], +parts[1] - 1, +parts[2], 0, 0, 0, 0)
      );
    });
    this.fullyClosedDates = dates;
  }

  private refreshClosureState(): void {
    const dateKey = this.getDateKey(this.date);
    if (!dateKey) {
      this.closedSessionKeys = new Set();
      this.isSelectedDateFullyClosed = false;
      this.selectedDateHasClosures = false;
      return;
    }

    const dateClosures = this.allClosures.filter(
      (entry) => this.closureDateToKey(entry.closed_date) === dateKey
    );
    const selectedDate = this.toLocalDate(this.date);
    this.isSelectedDateFullyClosed =
      this.isWeeklyClosed(selectedDate) || this.isDayFullyClosed(dateClosures);
    this.selectedDateHasClosures = dateClosures.length > 0;

    const salonName = String(this.selectedSalon || "").trim().toLowerCase();
    const partialClosures = dateClosures.filter(
      (entry) =>
        entry.session_name &&
        (!salonName ||
          !entry.salon_name ||
          String(entry.salon_name).trim().toLowerCase() === salonName)
    );
    this.closedSessionKeys = new Set(
      partialClosures.map((entry) =>
        String(entry.session_name).trim().toLowerCase()
      )
    );
  }

  private clearSelectedDateIfClosed(): void {
    const selectedDate = this.toLocalDate(this.date);
    if (!selectedDate) return;

    const dateKey = this.getDateKey(selectedDate);
    const explicitlyClosed = this.fullyClosedDates.some(
      (closedDate) => this.getDateKey(closedDate) === dateKey
    );
    if (!this.isWeeklyClosed(selectedDate) && !explicitlyClosed) return;

    this.date = "";
    this.selectedTableId = null;
    this.resetStepTwoSelections();
    this.refreshEffectiveSalons();
    this.refreshEffectiveSessions();
    this.clearTableAvailabilityState(
      "Calendar_Page.TABLE_AVAILABILITY_SELECT_DATE"
    );
  }

  private toLocalDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    const dateKey = this.getDateKey(value);
    if (!dateKey) return null;
    const [year, month, day] = dateKey.split("-").map(Number);
    const result = new Date(year, month - 1, day, 0, 0, 0, 0);
    return isNaN(result.getTime()) ? null : result;
  }

  loadSalonList(): void {
    const url = `${environment.baseUrl}/salon/get/all?user_id=${this.userId}`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.baseSalons = this.normalizeSalonRows(res);
        this.salonList = [...this.baseSalons];
        this.recomputeFullyClosedDates();
        this.clearSelectedDateIfClosed();
        this.refreshClosureState();
        this.refreshEffectiveSalons();
      },
      error: (err) => {
        console.error("Salon listesi alınamadı:", err);
      },
    });
  }

  loadSessions(): void {
    const url = `${environment.baseUrl}/salon/sessions?user_id=${this.userId}`;
    this.http.get<any>(url).subscribe({
      next: (data) => {
        this.baseSessions = this.normalizeSessionRows(data);
        this.sessions = [...this.baseSessions];
        this.fetchReservationDetails();
      },
      error: (err) => {
        console.error("Session çekilemedi:", err);
        this.fetchReservationDetails();
      },
    });
  }

  fetchReservationDetails(): void {
    console.log(
      "[fetchReservationDetails] Starting fetch for reservationId:",
      this.reservationId
    );

    const url = `${environment.baseUrl}/get-reservations?userId=${this.userId}`;
    console.log("[fetchReservationDetails] Fetching from URL:", url);

    this.http.get<any>(url).subscribe({
      next: (res) => {
        console.log("[fetchReservationDetails] Response received:", res);
        this.reservationRows = Array.isArray(res?.reservations)
          ? res.reservations
          : [];

        const reservation = this.reservationRows.find(
          (r: any) => r.reservation_id === this.reservationId
        );
        console.log(
          "[fetchReservationDetails] Matching reservation:",
          reservation
        );

        if (reservation) {
          const additionalInfo = this.normalizeAdditionalInfo(
            reservation.additional_info
          );
          this.name = additionalInfo["name"] || "";
          this.num_people =
            additionalInfo["num_people"] !== undefined
              ? Number(additionalInfo["num_people"])
              : null;
          this.service = additionalInfo["service"] || "";
          this.time = reservation.time.slice(0, 5); // remove ":00" if present
          this.date = new Date(reservation.date);
          this.selectedSalon = additionalInfo["salon_name"] || "";
          this.email = additionalInfo["email_address"] || "";
          this.phoneNumber = additionalInfo["customer_phone"] || "";
          const assignedTableId =
            reservation.table_id || additionalInfo["table_id"];
          this.selectedTableId =
            this.guestTableSelectionEnabled && assignedTableId
            ? String(assignedTableId)
            : null;

          // A closed walk-in (servis closed) is history like a past booking.
          this.pastReservation =
            isCompletedReservation(reservation) ||
            this.isReservationDateTimePast(reservation.date, reservation.time);
          if (this.pastReservation) {
            this.selectedSessionId = null;
            this.availableTimes = [];
            this.success = null;
            this.isLoading = false;
            return;
          }

          console.log("[fetchReservationDetails] Populated fields:", {
            name: this.name,
            num_people: this.num_people,
            service: this.service,
            time: this.time,
            date: this.date,
            selectedSalon: this.selectedSalon,
            email: this.email,
            selectedTableId: this.selectedTableId,
          });

          // match session id by time range — sort descending by start so a shared
          // boundary (e.g. 06:00) matches the session that starts there, not the one that ends there
          const sessionMatch = [...this.sessions]
            .sort((a, b) => (this.toMinutes(b.start_time) ?? 0) - (this.toMinutes(a.start_time) ?? 0))
            .find((session) =>
              this.isTimeInSession(reservation.time, session.start_time, session.end_time)
            );

          if (sessionMatch) {
            console.log(
              "[fetchReservationDetails] Found session match:",
              sessionMatch
            );
            this.selectedSessionId = sessionMatch.id;
            this.onSessionChange();
          } else {
            console.log("[fetchReservationDetails] No session match found.");
            this.updateTableAvailabilityPreview();
          }

          this.refreshEffectiveSessions();
          this.refreshClosureState();
          this.clearSelectedDateIfClosed();

          if (this.selectedSalon) {
            this.loadTablesForSalon(this.selectedSalon);
          } else {
            this.clearTableAvailabilityState(
              "Calendar_Page.TABLE_AVAILABILITY_SELECT_SALON"
            );
          }

          this.success = null;
        } else {
          console.warn(
            "[fetchReservationDetails] No reservation found with id:",
            this.reservationId
          );
          this.success = false;
        }
        this.isLoading = false;
        console.log(
          "[fetchReservationDetails] Finished, isLoading set to false."
        );
      },
      error: (err) => {
        console.error("[fetchReservationDetails] HTTP error:", err);
        this.success = false;
        this.isLoading = false;
      },
    });
  }

  onSessionChange(): void {
    this.stepValidationMessage = "";
    const selected = this.sessions.find((s) => s.id === this.selectedSessionId);
    if (!selected) {
      this.availableTimes = [];
      this.updateTableAvailabilityPreview();
      return;
    }

    const [startHour, startMin] = selected.start_time.split(":").map(Number);
    const [endHour, endMin] = selected.end_time.split(":").map(Number);

    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;

    const step = 15;
    const result: string[] = [];

    for (let mins = startTotal; mins <= endTotal; mins += step) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      result.push(
        `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`
      );
    }

    this.availableTimes = result;
    this.updateTableAvailabilityPreview();
  }

  onDateChange(value: Date | string): void {
    const newKey = this.getDateKey(value as any);
    if (newKey && newKey === this.getDateKey(this.date)) {
      return; // c-date-picker fires on init — date hasn't actually changed
    }
    this.stepValidationMessage = "";
    this.date = value as any;
    this.resetStepTwoSelections();
    this.currentStep = 1;
    console.log("[EditReservation][onDateChange]", {
      rawValue: value,
      parsedDateKey: this.getDateKey(this.date),
      selectedSalon: this.selectedSalon,
    });
    this.refreshEffectiveSalons();
    this.refreshEffectiveSessions();
    this.refreshClosureState();
    this.clearSelectedDateIfClosed();
    this.updateTableAvailabilityPreview();
  }

  onSalonChange(salonName: string): void {
    this.stepValidationMessage = "";
    this.selectedSalon = salonName || "";
    this.selectedTableId = null;
    this.resetStepTwoSelections();
    this.currentStep = 1;
    console.log("[EditReservation][onSalonChange]", {
      salonName: this.selectedSalon,
      dateKey: this.getDateKey(this.date),
    });

    if (!this.selectedSalon) {
      this.selectedSalonTables = [];
      this.clearTableAvailabilityState(
        "Calendar_Page.TABLE_AVAILABILITY_SELECT_SALON"
      );
      this.refreshEffectiveSessions();
      return;
    }

    this.refreshEffectiveSessions();
    this.refreshClosureState();
    this.loadTablesForSalon(this.selectedSalon);
  }

  onTableAvailabilityBoxClick(dot: TableAvailabilityDot): void {
    if (!this.guestTableSelectionEnabled) return;
    if (!dot?.isAvailable || !dot.isNearestFit) return;
    this.selectedTableId =
      this.selectedTableId === dot.tableId ? null : dot.tableId;
    this.stepValidationMessage = "";
  }

  clearTableSelection(): void {
    this.selectedTableId = null;
    this.stepValidationMessage = "";
  }

  compareById(a: any, b: any): boolean {
    return a === b;
  }

  formatDate(date: Date, lang: "TR" | "EN"): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return lang === "TR"
      ? `${day}.${month}.${year}`
      : `${year}-${month}-${day}`;
  }

  private getEmailDate(date: Date): string {
    const lang = this.translate.currentLang || "en";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return (lang === "tr" || lang === "de") ? `${day}.${month}.${year}` : `${day}/${month}/${year}`;
  }

  formatLocalDate(date: Date): string {
    return (
      date.getFullYear() +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getDate()).padStart(2, "0")
    );
  }

  sendReservationConfirmationEmail(): void {
    const dateFormatted = this.getEmailDate(this.date);
    const cancelLink = `${environment.baseUrl}/#/cancel/${encodeURIComponent(this.companyName)}/${this.reservationId}`;
    const editLink = `${environment.baseUrl}/#/edit/${encodeURIComponent(this.companyName)}/${this.reservationId}`;

    const t = (key: string, params?: object) =>
      this.translate.instant(`EMAIL_CONFIRMATION.${key}`, params);

    const bodyText = t('BODY_UPDATE', {
      numPeople: `<strong>${this.num_people}</strong>`,
      date: `<strong>${dateFormatted}</strong>`,
      time: `<strong>${this.time}</strong>`,
      salon: `<strong>${this.selectedSalon}</strong>`,
    });

    const guardsBlock = this.guards.length
      ? `
              <table width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:30px;">
                <tr>
                  <td style="padding:14px 18px;background:#f7f9ff;border:1px solid #dbe4ff;border-radius:14px;">
                    <div style="font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#5f6a87;font-weight:600;margin-bottom:10px;">
                      ${t('GUARDS_TITLE')}
                    </div>
                    ${this.guards
                      .map(
                        (guard) => `
                    <div style="font-size:13px;line-height:1.5;color:#1f2a44;margin-bottom:6px;">
                      &#10003; ${this.escapeHtml(guard.text)}
                    </div>`
                      )
                      .join('')}
                  </td>
                </tr>
              </table>`
      : '';

    const message = `
      <div style="font-family: 'Inter', Arial, sans-serif; background:#f4f6fb; padding:32px 16px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;margin:0 auto;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 18px 45px rgba(9,21,54,0.12);">
          <tr>
            <td style="background:linear-gradient(130deg,#01030d,#08153a);padding:28px 32px;text-align:center;color:#fff;">
              <div style="font-size:12px;letter-spacing:0.4em;text-transform:uppercase;opacity:0.7;">${t('HEADER_UPDATE')}</div>
              <h1 style="margin:12px 0 0;font-size:26px;">${this.companyName}</h1>
              <div style="margin-top:6px;font-size:13px;opacity:.8;">ID ${this.reservationId}</div>
            </td>
          </tr>
          <tr>
            <td style="padding:30px 32px;color:#1b2439;">
              <p style="margin:0 0 30px;font-size:15px;line-height:1.6;">${bodyText}</p>

              <table width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:30px;">
                <tr>
                  <td style="padding:14px 18px;background:#f3f6ff;border-radius:14px;">
                    <table width="100%" cellspacing="0" cellpadding="0" style="font-size:14px;line-height:1.4;color:#1f2a44;">
                      <tr>
                        <td style="width:40%;">${t('DATE_LABEL')}</td>
                        <td><strong>${dateFormatted}</strong></td>
                      </tr>
                      <tr>
                        <td style="padding-top:8px;">${t('TIME_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${this.time}</strong></td>
                      </tr>
                      <tr>
                        <td style="padding-top:8px;">${t('PARTY_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${this.num_people} ${t('GUESTS_SUFFIX')}</strong></td>
                      </tr>
                      <tr>
                        <td style="padding-top:8px;">${t('SALON_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${this.selectedSalon}</strong></td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
${guardsBlock}

              <div style="text-align:center;margin:0 0 24px;">
                <a href="${editLink}"
                  style="display:inline-block;min-width:160px;padding:11px 22px;border-radius:8px;background:#1a3d8f;border:none;color:#ffffff;text-decoration:none;font-weight:600;font-size:13px;line-height:1.4;margin:6px;">
                  ${t('EDIT_BUTTON')}
                </a>
                <a href="${cancelLink}"
                  style="display:inline-block;min-width:160px;padding:11px 22px;border-radius:8px;background:#c03535;border:none;color:#ffffff;text-decoration:none;font-weight:600;font-size:13px;line-height:1.4;margin:6px;">
                  ${t('CANCEL_BUTTON')}
                </a>
              </div>

              <p style="font-size:12px;color:#6c7794;text-align:center;">${t('HELP_TEXT_UPDATE')}</p>
            </td>
          </tr>
          <tr>
            <td style="background:#fafbff;padding:18px 24px;text-align:center;font-size:12px;color:#7f8aac;">
              <div style="letter-spacing:0.3em;text-transform:uppercase;">${t('POWERED_BY')}</div>
            </td>
          </tr>
        </table>
      </div>
    `;

    const emailPayload = {
      header: `Reservation Update ${this.reservationId}`,
      content: reservationTicketEmail(message,this.ticketLink,this.translate.instant('RESERVATION_TICKET.VIEW_MANAGE')),
      to_addr: this.email,
    };

    this.http
      .post(`${environment.baseUrl}/email_notifications/info`, emailPayload)
      .subscribe({
        next: () => console.log("Update confirmation email sent successfully."),
        error: (err) => console.error("Failed to send update email:", err),
      });
  }

  private escapeHtml(value: string): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  saveChanges(form?: NgForm): void {
    if (this.isLoading) return;

    if (this.isReservationDateTimePast(this.date, this.time)) {
      this.pastReservation = true;
      this.success = null;
      this.isLoading = false;
      return;
    }

    if (!this.isStepOneValid()) {
      this.currentStep = 1;
      this.stepValidationMessage = this.stepOneValidationMessage();
      return;
    }
    if (!this.isStepTwoValid()) {
      this.currentStep = 2;
      this.stepValidationMessage = this.stepTwoValidationMessage();
      return;
    }
    if (!this.areContactFieldsValid()) {
      this.currentStep = 3;
      if (form) {
        this.markAllAsTouched(form);
        this.scrollToFirstInvalid();
      }
      this.stepValidationMessage = this.translate.instant(
        "EDIT_RESERVATION.VALIDATION_STEP_THREE"
      );
      return;
    }
    // Prevent submit if form invalid; show first error clearly
    if (form && form.invalid) {
      this.markAllAsTouched(form);
      this.scrollToFirstInvalid();
      return;
    }
    if (!this.captchaReady) {
      this.currentStep = 3;
      this.stepValidationMessage = this.translate.instant("CAPTCHA.LOADING");
      return;
    }

    this.stepValidationMessage = "";
    this.isLoading = true;

    let finalTime = this.time;
    if (finalTime && finalTime.length === 5) {
      finalTime += ":00";
    }

    const formattedDate = this.getDateKey(this.date);
    if (!formattedDate) {
      this.errorMessage = this.translate.instant("Calendar_Page.UPDATE_FAILED");
      this.showAlert(this.errorMessage);
      this.success = false;
      this.isLoading = false;
      return;
    }
    const payload: any = {
      time: finalTime,
      date: formattedDate,
      num_people: this.num_people,
      name: this.name?.trim(),
      service: this.service,
      salon_name: this.selectedSalon,
      session_id: this.selectedSessionId,
      session_name:
        this.sessions.find((session) => session.id === this.selectedSessionId)
          ?.name || null,
      source: "online booking form",
      source_id: this.email?.trim(),
      email_address: this.email?.trim(),
      customer_phone: this.normalizePhoneNumber(this.phoneNumber),
      notes: this.service?.trim() || null,
      on_conflict: "reject",
      acknowledged_guard_ids: this.guards.map((guard) => guard.id),
      captcha_token: this.captchaToken,
    };
    if (this.guestTableSelectionEnabled) {
      payload.table_id = this.selectedTableId;
      payload.tableId = this.selectedTableId;
    }

    // Guest-facing public endpoint: the shared dashboard route is JWT-only
    // now; guests go through the flag-gated /reservations/public/... path.
    const url = `${environment.baseUrl}/reservations/public/users/${this.userId}/reservations/${this.reservationId}/update`;
    this.http.post<any>(url, payload).subscribe({
      next: (res) => {
        this.ticketLink=res?.ticket_link||null;
        this.errorMessage = "";
        this.success = true;
        this.isLoading = false;
        this.sendReservationConfirmationEmail();
      },
      error: (err) => {
        if (err?.status === 403 && err?.error?.code === "EDIT_DISABLED") {
          // Tenant switched guest editing off after the form loaded.
          this.guestEditDisabled = true;
          this.disabledMessage = err.error?.message || null;
          this.success = null;
          this.isLoading = false;
          return;
        }
        if (isCompletedRefusal(err)) {
          // The servis closed after the form loaded: show the closed state.
          this.pastReservation = true;
          this.success = null;
          this.isLoading = false;
          return;
        }
        console.error("Update failed:", err);
        this.errorMessage = this.extractErrorMessage(
          err,
          "Calendar_Page.UPDATE_FAILED"
        );
        this.showAlert(this.errorMessage);
        this.success = false;
        this.isLoading = false;
        this.altcha?.reset();
      },
    });
  }

  private extractErrorMessage(error: any, fallbackKey: string): string {
    const messageCandidates: Array<string | undefined> = [
      error?.error?.message,
      typeof error?.error === "string" ? error.error : undefined,
      error?.message,
    ];

    for (const candidate of messageCandidates) {
      const value = String(candidate || "").trim();
      if (value) {
        return value;
      }
    }

    const translatedFallback = this.translate.instant(fallbackKey);
    if (
      translatedFallback &&
      translatedFallback !== fallbackKey &&
      translatedFallback.trim()
    ) {
      return translatedFallback;
    }

    return this.translate.instant("EDIT_RESERVATION.ALERT_ERROR_TEXT");
  }

  private showAlert(message: string): void {
    alert(message);
  }

  private refreshEffectiveSessions(): void {
    const token = ++this.sessionRequestToken;
    const dateKey = this.getDateKey(this.date);
    if (!this.userId || !dateKey) {
      this.sessions = [...this.baseSessions];
      this.refreshClosureState();
      this.ensureSelectedSessionValidity();
      return;
    }

    this.http
      .get<any>(`${environment.baseUrl}/salon/sessions/effective`, {
        params: {
          user_id: String(this.userId),
          reservation_date: dateKey,
          ...(this.selectedSalon ? { salon_name: this.selectedSalon } : {}),
        },
      })
      .subscribe({
        next: (res) => {
          if (token !== this.sessionRequestToken) return;
          this.sessions = this.normalizeSessionRows(res);
          this.refreshClosureState();
          this.ensureSelectedSessionValidity();
        },
        error: () => {
          if (token !== this.sessionRequestToken) return;
          this.sessions = [...this.baseSessions];
          this.refreshClosureState();
          this.ensureSelectedSessionValidity();
        },
      });
  }

  private refreshEffectiveSalons(): void {
    const dateKey = this.getDateKey(this.date);
    if (!this.userId || !dateKey) {
      this.salonList = [...this.baseSalons];
      return;
    }

    this.http
      .get<any>(`${environment.baseUrl}/salon/effective`, {
        params: {
          user_id: String(this.userId),
          reservation_date: dateKey,
        },
      })
      .subscribe({
        next: (res) => {
          const rows = this.normalizeSalonRows(res);
          this.salonList = rows;
          if (
            this.selectedSalon &&
            !this.salonList.some((s) => s.salon_name === this.selectedSalon)
          ) {
            this.selectedSalon = "";
            this.refreshEffectiveSessions();
          }
        },
        error: () => {
          this.salonList = [...this.baseSalons];
        },
      });
  }


  private ensureSelectedSessionValidity(): void {
    if (this.selectedSessionId) {
      const selected = this.sessions.find(
        (session) => session.id === this.selectedSessionId
      );
      if (!selected || this.isSessionClosed(selected)) {
        this.selectedSessionId = null;
        this.time = "";
        this.availableTimes = [];
        return;
      }
      this.onSessionChange();
    }
  }

  private normalizeSessionRows(response: any): Session[] {
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.sessions)
        ? response.sessions
        : Array.isArray(response)
          ? response
          : [];

    return rows
      .map((row: any) => ({
        id: Number(row?.id),
        user_id: Number(row?.user_id || this.userId || 0),
        name: String(row?.name || "").trim(),
        start_time: String(row?.start_time || "").trim(),
        end_time: String(row?.end_time || "").trim(),
        hall_id: Number(row?.hall_id || 0),
      }))
      .filter(
        (row: Session) =>
          Number.isFinite(row.id) &&
          !!row.name &&
          !!row.start_time &&
          !!row.end_time
      );
  }

  private normalizeSalonRows(response: any): SalonOption[] {
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.salons)
        ? response.salons
        : Array.isArray(response)
          ? response
          : [];

    const names = rows
      .map((row: any) =>
        typeof row === "string"
          ? row.trim()
          : String(row?.salon_name || "").trim()
      )
      .filter((name: string) => !!name);

    const unique: Record<string, string> = {};
    names.forEach((name: string) => {
      const key = name.toLowerCase();
      if (!unique[key]) {
        unique[key] = name;
      }
    });

    return Object.values(unique)
      .sort((a, b) => a.localeCompare(b))
      .map((salon_name) => ({ salon_name }));
  }

  private loadTablesForSalon(salonName: string): void {
    if (!this.guestTableSelectionEnabled) {
      this.selectedSalonTables = [];
      this.selectedTableId = null;
      this.clearTableAvailabilityState();
      return;
    }
    if (!salonName) {
      this.selectedSalonTables = [];
      this.clearTableAvailabilityState(
        "Calendar_Page.TABLE_AVAILABILITY_SELECT_SALON"
      );
      return;
    }

    const cached = this.salonTablesCache[salonName];
    if (cached) {
      this.selectedSalonTables = cached;
      this.updateTableAvailabilityPreview();
      return;
    }

    this.isTableAvailabilityLoading = true;
    const url = `${environment.baseUrl}/salon/tables?user_id=${this.userId}&salon_name=${encodeURIComponent(
      salonName
    )}`;
    this.http.get<SalonTable[]>(url).subscribe({
      next: (tables) => {
        const rows = Array.isArray(tables) ? tables : [];
        this.salonTablesCache[salonName] = rows;
        if (this.selectedSalon === salonName) {
          this.selectedSalonTables = rows;
          this.updateTableAvailabilityPreview();
        }
      },
      error: (err) => {
        console.error("Failed to load tables for salon:", err);
        if (this.selectedSalon === salonName) {
          this.selectedSalonTables = [];
          this.clearTableAvailabilityState(
            "Calendar_Page.TABLE_AVAILABILITY_ERROR"
          );
        }
      },
    });
  }

  private updateTableAvailabilityPreview(): void {
    if (!this.guestTableSelectionEnabled) {
      this.selectedTableId = null;
      this.clearTableAvailabilityState();
      return;
    }
    if (!this.selectedSalon) {
      this.clearTableAvailabilityState(
        "Calendar_Page.TABLE_AVAILABILITY_SELECT_SALON"
      );
      return;
    }

    if (!this.selectedSalonTables.length) {
      if (!this.isTableAvailabilityLoading) {
        this.clearTableAvailabilityState(
          "Calendar_Page.TABLE_AVAILABILITY_NO_TABLES"
        );
      }
      return;
    }

    const selectedSession = this.sessions.find(
      (session) => session.id === this.selectedSessionId
    );
    if (!selectedSession) {
      this.clearTableAvailabilityState(
        "Calendar_Page.TABLE_AVAILABILITY_SELECT_SESSION"
      );
      return;
    }

    const dateKey = this.getDateKey(this.date);
    if (!dateKey) {
      this.clearTableAvailabilityState(
        "Calendar_Page.TABLE_AVAILABILITY_SELECT_DATE"
      );
      return;
    }

    const cancelledStatuses = ['cancelled', 'canceled', 'cancel', 'iptal', 'storno'];
    const reservedTableIds = new Set(
      this.reservationRows
        .filter((res) => res?.reservation_id !== this.reservationId)
        .filter((res) => {
          const s = String(res?.status || res?.reservation_status || '').trim().toLowerCase();
          return !cancelledStatuses.some((t) => s.includes(t));
        })
        .filter((res) => this.getDateKey(res?.date) === dateKey)
        .filter((res) => {
          const info = this.normalizeAdditionalInfo(res?.additional_info);
          return String((res?.salon_name || info?.["salon_name"]) || "") === this.selectedSalon;
        })
        .filter((res) =>
          this.isTimeInSession(
            res?.time,
            selectedSession.start_time,
            selectedSession.end_time
          )
        )
        .map((res) => {
          const info = this.normalizeAdditionalInfo(res?.additional_info);
          return res?.table_id ?? info?.["table_id"];
        })
        .filter((tableId) => tableId !== undefined && tableId !== null && `${tableId}`.trim() !== "")
        .map((tableId) => String(tableId))
    );

    this.loadEffectiveTablesForAvailability(
      reservedTableIds,
      dateKey,
      selectedSession.name,
    );
  }

  /**
   * Joined tables are date/session-specific. The base salon table endpoint
   * cannot tell us which source tables have been replaced by a combination,
   * so the public edit form must use the same effective-table projection as
   * the other reservation forms.
   */
  private loadEffectiveTablesForAvailability(
    reservedTableIds: Set<string>,
    dateKey: string,
    sessionName: string,
  ): void {
    const requestToken = ++this.tableAvailabilityRequestToken;
    const fallbackTables = this.selectedSalonTables;

    if (!this.userId || !this.selectedSalon) {
      this.applyTableAvailabilityFromTables(fallbackTables, reservedTableIds);
      return;
    }

    this.isTableAvailabilityLoading = true;
    this.http
      .get<any>(`${environment.baseUrl}/restaurant-table-combinations/effective`, {
        params: {
          user_id: String(this.userId),
          salon_name: this.selectedSalon,
          reservation_date: dateKey,
          session_name: sessionName,
          ...(this.time ? { reservation_time: this.time } : {}),
        },
      })
      .subscribe({
        next: (response) => {
          if (requestToken !== this.tableAvailabilityRequestToken) return;
          const tables = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];
          this.applyTableAvailabilityFromTables(
            tables.length ? tables : fallbackTables,
            reservedTableIds,
          );
        },
        error: (err) => {
          if (requestToken !== this.tableAvailabilityRequestToken) return;
          console.error(
            "Failed to load effective tables for edit availability:",
            err,
          );
          this.applyTableAvailabilityFromTables(fallbackTables, reservedTableIds);
        },
      });
  }

  private applyTableAvailabilityFromTables(
    tables: SalonTable[],
    reservedTableIds: Set<string>
  ): void {
    const normalizedTables = Array.isArray(tables) ? tables : [];
    if (!normalizedTables.length) {
      this.clearTableAvailabilityState(
        "Calendar_Page.TABLE_AVAILABILITY_NO_TABLES"
      );
      return;
    }

    const partySize = Number(this.num_people);
    const baseTableIds = new Set(
      this.selectedSalonTables.map((table) => String(table.table_id)),
    );
    let joinedTableIndex = 0;
    const base = [...normalizedTables]
      .sort((a, b) =>
        String(a.table_id).localeCompare(String(b.table_id), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      )
      .map((table) => {
        const capacity = Number(table.capacity);
        const isCombined =
          table.is_combined === true ||
          (table.combination_id !== undefined &&
            table.combination_id !== null) ||
          (Array.isArray(table.source_table_ids) &&
            table.source_table_ids.length > 0) ||
          // Privacy-safe fallback: an effective table that does not exist in
          // the salon's base table list is a generated combination. Mask it
          // even if an older backend omitted the combination metadata.
          !baseTableIds.has(String(table.table_id));
        return {
          tableId: String(table.table_id),
          isAvailable: !reservedTableIds.has(String(table.table_id)),
          capacity: Number.isFinite(capacity) ? capacity : undefined,
          isCombined,
          joinedTableIndex: isCombined ? ++joinedTableIndex : undefined,
        };
      });

    // Only tables close to the smallest table that still fits the party are
    // offered — a large table is hard-blocked when a snugger one is free.
    const bestFit = nearestFitCapacity(base, partySize);
    const dots = base
      .map((table) => ({
        ...table,
        canFitParty:
          Number.isFinite(Number(table.capacity)) &&
          Number(table.capacity) >= 1 &&
          Number.isFinite(partySize) &&
          partySize >= 1 &&
          Number(table.capacity) >= partySize,
        isNearestFit: isNearestFitTable(table.capacity, partySize, bestFit),
      }))
      .sort((a, b) =>
        a.tableId.localeCompare(b.tableId, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );

    this.tableAvailabilityDots = dots;
    if (
      this.selectedTableId &&
      !dots.some(
        (dot) =>
          dot.tableId === this.selectedTableId &&
          dot.isAvailable &&
          dot.isNearestFit
      )
    ) {
      this.selectedTableId = null;
    }
    this.tableAvailabilitySummary = {
      total: dots.length,
      available: dots.filter((dot) => dot.isAvailable).length,
      occupied: dots.filter((dot) => !dot.isAvailable).length,
    };
    this.tableAvailabilityMessageKey = null;
    this.isTableAvailabilityLoading = false;
  }

  private clearTableAvailabilityState(messageKey: string | null = null): void {
    this.tableAvailabilityDots = [];
    this.tableAvailabilitySummary = { total: 0, available: 0, occupied: 0 };
    this.tableAvailabilityMessageKey = messageKey;
    this.isTableAvailabilityLoading = false;
  }

  private normalizeAdditionalInfo(
    value: Record<string, any> | string | null | undefined
  ): Record<string, any> {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return value;
  }

  private getDateKey(value: Date | string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      if (isNaN(value.getTime())) {
        return null;
      }
      return format(value, "yyyy-MM-dd");
    }

    const raw = String(value).trim();
    if (!raw) {
      return null;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split(/[./]/);
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
      return null;
    }
    return format(parsed, "yyyy-MM-dd");
  }

  private isReservationDateTimePast(
    dateValue: Date | string | null | undefined,
    timeValue: string | null | undefined
  ): boolean {
    const dateKey = this.getDateKey(dateValue);
    const timeMatch = String(timeValue || "")
      .trim()
      .match(/^(\d{1,2}):(\d{2})/);
    if (!dateKey || !timeMatch) return false;

    const [year, month, day] = dateKey.split("-").map(Number);
    const hours = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]);
    const scheduledAt = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (isNaN(scheduledAt.getTime())) return false;

    return scheduledAt.getTime() < Date.now();
  }

  private isTimeInSession(
    time: string | null | undefined,
    sessionStart: string,
    sessionEnd: string
  ): boolean {
    const timeMinutes = this.toMinutes(time);
    const startMinutes = this.toMinutes(sessionStart);
    const endMinutes = this.toMinutes(sessionEnd);

    if (timeMinutes === null || startMinutes === null || endMinutes === null) {
      return false;
    }
    return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
  }

  private toMinutes(value: string | null | undefined): number | null {
    if (!value) return null;
    const raw = value.split(":");
    if (raw.length < 2) return null;
    const hours = Number(raw[0]);
    const minutes = Number(raw[1]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return hours * 60 + minutes;
  }

  private markAllAsTouched(form: NgForm) {
    Object.values(form.controls).forEach((c) => c.markAsTouched());
  }

  get partySizeExceeded(): boolean {
    const limit = Number(this.formSettings?.max_people);
    if (!Number.isFinite(limit) || limit < 1) {
      return false;
    }
    return Number(this.num_people) > limit;
  }

  partySizeLimitMessage(): string {
    const custom = String(this.formSettings?.max_people_message || "").trim();
    if (custom) {
      return custom;
    }
    return this.translate.instant("EDIT_RESERVATION.MAX_PEOPLE_DEFAULT", {
      max: this.formSettings?.max_people,
    });
  }

  toggleGuardConsent(guard: BookingFormGuard): void {
    this.guardConsents[guard.id] = !this.guardConsents[guard.id];
    this.stepValidationMessage = "";
  }

  allGuardsAccepted(): boolean {
    return (
      this.guardsLoaded &&
      this.guards.every((guard) => this.guardConsents[guard.id] === true)
    );
  }

  onPeopleChange(): void {
    this.stepValidationMessage = "";
    this.resetStepTwoSelections();
    this.currentStep = 1;
    this.updateTableAvailabilityPreview();
  }

  nextStep(): void {
    if (!this.canProceedFromCurrentStep()) {
      return;
    }
    if (this.currentStep < this.totalSteps) {
      this.currentStep += 1;
    }
  }

  prevStep(): void {
    this.stepValidationMessage = "";
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  goToStep(step: number): void {
    if (step < 1 || step > this.totalSteps || step === this.currentStep) {
      return;
    }
    if (step === 1) {
      this.currentStep = 1;
      return;
    }
    if (step === 2 && this.isStepOneValid()) {
      this.stepValidationMessage = "";
      this.currentStep = 2;
      return;
    }
    if (step === 3 && this.isStepOneValid() && this.isStepTwoValid()) {
      this.stepValidationMessage = "";
      this.currentStep = 3;
    }
  }

  canAccessStep(step: number): boolean {
    if (step <= 1) {
      return true;
    }
    if (step === 2) {
      return this.isStepOneValid();
    }
    if (step === 3) {
      return this.isStepOneValid() && this.isStepTwoValid();
    }
    return false;
  }

  trackBySalonName(_index: number, salon: SalonOption): string {
    return salon.salon_name;
  }

  isSessionClosed(session: Session): boolean {
    if (this.isSelectedDateFullyClosed) return true;
    return this.closedSessionKeys.has(
      String(session.name || "").trim().toLowerCase()
    );
  }

  selectSession(session: Session): void {
    if (this.isSessionClosed(session)) return;
    this.selectedSessionId = session.id;
    this.stepValidationMessage = '';
    this.onSessionChange();
  }

  selectStartTime(time: string): void {
    this.time = time;
    this.stepValidationMessage = '';
  }

  isStepOneValid(): boolean {
    const hasDate = !!this.getDateKey(this.date);
    const hasSalon = !!String(this.selectedSalon || "").trim();
    const hasPeople = Number(this.num_people) >= 1;
    return (
      hasDate &&
      hasSalon &&
      hasPeople &&
      !this.isSelectedDateClosed() &&
      !this.partySizeExceeded &&
      this.allGuardsAccepted()
    );
  }

  isStepTwoValid(): boolean {
    const selectedSession = this.sessions.find(
      (session) => session.id === this.selectedSessionId
    );
    return (
      !!selectedSession &&
      !this.isSessionClosed(selectedSession) &&
      !!this.time &&
      this.isSelectedTableValid()
    );
  }

  private isSelectedDateClosed(): boolean {
    const selectedDate = this.toLocalDate(this.date);
    const dateKey = this.getDateKey(selectedDate);
    if (!selectedDate || !dateKey) return false;
    if (this.isWeeklyClosed(selectedDate)) return true;

    const dateClosures = this.allClosures.filter(
      (entry) => this.closureDateToKey(entry.closed_date) === dateKey
    );
    return this.isDayFullyClosed(dateClosures);
  }

  isStepThreeValid(): boolean {
    return this.areContactFieldsValid();
  }

  private areContactFieldsValid(): boolean {
    const hasName = !!String(this.name || "").trim();
    const emailValue = String(this.email || "").trim();
    const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
    const phone = this.normalizePhoneNumber(this.phoneNumber);
    const hasPhone = /^[0-9+()\-\s]{7,20}$/.test(phone);
    return hasName && hasEmail && hasPhone;
  }

  isSelectedTableValid(): boolean {
    if (!this.guestTableSelectionEnabled) {
      return true;
    }
    if (!this.selectedTableId) {
      return false;
    }
    const selected = this.tableAvailabilityDots.find(
      (dot) => dot.tableId === this.selectedTableId
    );
    return !!selected?.isAvailable && selected.isNearestFit;
  }

  private stepOneValidationMessage(): string {
    if (this.partySizeExceeded) {
      return this.partySizeLimitMessage();
    }
    const hasDate = !!this.getDateKey(this.date);
    const hasSalon = !!String(this.selectedSalon || "").trim();
    const hasPeople = Number(this.num_people) >= 1;
    if (hasDate && hasSalon && hasPeople && !this.allGuardsAccepted()) {
      return this.translate.instant("EDIT_RESERVATION.VALIDATION_GUARDS");
    }
    return this.translate.instant("EDIT_RESERVATION.VALIDATION_STEP_ONE");
  }

  private stepTwoValidationMessage(): string {
    if (this.selectedSessionId === null || !this.time) {
      return this.translate.instant("EDIT_RESERVATION.VALIDATION_STEP_TWO");
    }
    return this.translate.instant("EDIT_RESERVATION.VALIDATION_TABLE");
  }

  private normalizePhoneNumber(value: string): string {
    return String(value || '').trim();
  }

  private canProceedFromCurrentStep(): boolean {
    this.stepValidationMessage = "";
    if (this.currentStep === 1 && !this.isStepOneValid()) {
      this.stepValidationMessage = this.stepOneValidationMessage();
      return false;
    }
    if (this.currentStep === 2 && !this.isStepTwoValid()) {
      this.stepValidationMessage = this.stepTwoValidationMessage();
      return false;
    }
    return true;
  }

  private resetStepTwoSelections(): void {
    this.selectedSessionId = null;
    this.time = "";
    this.availableTimes = [];
  }

  private scrollToFirstInvalid() {
    // Find first .ng-invalid control inside this component and scroll to it
    setTimeout(() => {
      const el = document.querySelector(
        '.ng-invalid[aria-required="true"], .ng-invalid[required]'
      );
      if (el && "scrollIntoView" in el) {
        (el as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        (el as HTMLElement).focus?.();
      }
    });
  }
}
